/**
 * Production-Grade CSRF Manager
 * Handles CSRF token management with proper error handling and fallbacks
 */
import finalConfig from "../apiConfig";
class CSRFManager {
  constructor() {
    this.state = {
      token: null,
      isInitialized: false,
      isFetching: false,
      lastFetchTime: 0,
      fetchPromise: null,
      retryCount: 0
    };
    this.CACHE_DURATION = 55 * 60 * 1000; // 55 minutes
    this.MAX_RETRIES = 3;
    this.RETRY_DELAY = 1000; // 1 second
    this.listeners = new Set();
  }

  // Subscription pattern for state updates
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notify(token) {
    this.listeners.forEach(cb => {
      try {
        cb(token);
      } catch (error) {
        console.warn('[CSRF] Listener callback error:', error);
      }
    });
  }

  // Extract CSRF token from cookie (production-safe)
  getTokenFromCookie() {
    try {
      const cookies = document.cookie.split(';');
      const csrfCookie = cookies.find(cookie =>
        cookie.trim().startsWith('csrf_token=')
      );
      if (csrfCookie) {
        const token = decodeURIComponent(csrfCookie.split('=')[1].trim());
        return token || null;
      }
      return null;
    } catch (error) {
      console.warn('[CSRF] Cookie parsing error:', error);
      return null;
    }
  }

  // Get current token with fallback strategy
  getCurrentToken() {
    return this.state.token || this.getTokenFromCookie();
  }

  // Check if token needs refresh
  needsRefresh() {
    if (!this.state.isInitialized) return true;
    
    const currentToken = this.getCurrentToken();
    if (!currentToken) return true;
    
    const age = Date.now() - this.state.lastFetchTime;
    return age > this.CACHE_DURATION;
  }

  // Main token fetch method with retry logic
  async fetchToken(force = false) {
    if (!force && !this.needsRefresh()) {
      const token = this.getCurrentToken();
      if (token) {
        return token;
      }
    }

    // Prevent duplicate fetches
    if (this.state.isFetching && this.state.fetchPromise) {
      return this.state.fetchPromise;
    }

    this.state.isFetching = true;
    this.state.fetchPromise = this._performFetchWithRetry()
      .finally(() => {
        this.state.isFetching = false;
        this.state.fetchPromise = null;
      });

    return this.state.fetchPromise;
  }

  // Fetch with retry logic
  async _performFetchWithRetry() {
    let lastError;
    
    for (let attempt = 0; attempt < this.MAX_RETRIES; attempt++) {
      try {
        const token = await this._performFetch();
        this.state.retryCount = 0; // Reset on success
        return token;
      } catch (error) {
        lastError = error;
        console.warn(`[CSRF] Fetch attempt ${attempt + 1} failed:`, error.message);
        
        if (attempt < this.MAX_RETRIES - 1) {
          await this._delay(this.RETRY_DELAY * Math.pow(2, attempt)); // Exponential backoff
        }
      }
    }
    
    this.state.retryCount++;
    throw new Error(`CSRF token fetch failed after ${this.MAX_RETRIES} attempts: ${lastError?.message || 'Unknown error'}`);
  }

  
  // Core fetch implementation
  async _performFetch() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const rawBase = finalConfig.API.baseUrl;
      const base = rawBase.replace(/\/$/, '');   // <- normalize once

      // Health first
      let response;
      try {
        response = await fetch(`${base}/health/live`, {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
      } catch (healthError) {
        console.warn('[CSRF] Health check failed, trying fallback endpoint');
        response = await fetch(`${base}/users/me`, {   // <- uses normalized base too
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
      }

      clearTimeout(timeoutId);

      // Check if request succeeded
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Get token from cookie (set by middleware)
      const token = this.getTokenFromCookie();
      
      if (!token) {
        throw new Error('CSRF token not found in cookies after request');
      }

      // Validate token format
      if (typeof token !== 'string' || token.length < 10) {
        throw new Error('Invalid CSRF token format');
      }

      // Update state
      this.state.token = token;
      this.state.isInitialized = true;
      this.state.lastFetchTime = Date.now();
      
      // Notify subscribers
      this.notify(token);

      //console.log('[CSRF] Token obtained successfully');
      return token;
      
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('CSRF token fetch timed out');
      }
      
      console.warn('[CSRF] Fetch failed:', error.message);
      throw error;
    }
  }


  // Initialize on app startup
  async initialize() {
    if (this.state.isInitialized) {
      return this.getCurrentToken();
    }
    
    // Check for existing valid token first
    const existingToken = this.getTokenFromCookie();
    if (existingToken && this._isTokenValid(existingToken)) {
      this.state.token = existingToken;
      this.state.isInitialized = true;
      this.state.lastFetchTime = Date.now();
      //console.log('[CSRF] Using existing valid token');
      return existingToken;
    }
    
    // Fetch new token
    try {
      return await this.fetchToken();
    } catch (error) {
      console.warn('[CSRF] Initialization failed:', error.message);
      // Don't throw - allow app to continue in degraded mode
      this.state.isInitialized = true; // Mark as initialized to prevent infinite retries
      return null;
    }
  }

  // Force refresh (after login/logout)
  async refresh() {
    try {
      return await this.fetchToken(true);
    } catch (error) {
      console.warn('[CSRF] Refresh failed:', error.message);
      throw error;
    }
  }

  // Reset state (on logout or error)
  reset() {
    this.state.token = null;
    this.state.isInitialized = false;
    this.state.lastFetchTime = 0;
    this.state.retryCount = 0;
    this.notify(null);
    //console.log('[CSRF] State reset');
  }

  // Get status for debugging
  getStatus() {
    return {
      initialized: this.state.isInitialized,
      hasToken: !!this.getCurrentToken(),
      lastFetchTime: this.state.lastFetchTime,
      retryCount: this.state.retryCount,
      isFetching: this.state.isFetching
    };
  }

  // Utility methods
  _isTokenValid(token) {
    return typeof token === 'string' && token.length >= 10;
  }

  _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const csrfManager = new CSRFManager();
export default csrfManager;